import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { TrashIcon, CheckIcon, ArrowsRightLeftIcon, Cog6ToothIcon, ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import { toast } from 'react-hot-toast';
import SpinnerLoad from 'views/finance/moduleComponents/SpinnerLoad';
import ModalConfirm from 'views/finance/moduleComponents/ModalConfirm';
import { fetchFlowAccounts, fetchFlowAccountsHistory, deleteFlowAccountHistory, createFlowAccountHistory, updateFlowAccountHistory, deleteAllFlowAccountHistory, createBatchFlowAccountHistory } from 'views/finance/moduleServices/flow_requests';
import CustomModalAccountsManage from './CustomModalAccountsManage';
import CustomAccountsCrosscheck from './CustomAccountsCrosscheck';

// ##### UPDATE BATCH CARD - ACCOUNTS CHECK
const CustomAccountsStatus = forwardRef(({ reload }, ref) => {
  const [accounts, setAccounts] = useState([]);
  const [accountsHistory, setAccountsHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState({});
  const [addingHistory, setAddingHistory] = useState(false);
  const [newDateInput, setNewDateInput] = useState('');
  const [showDeleteDateModal, setShowDeleteDateModal] = useState(false);
  const [dateToDelete, setDateToDelete] = useState(null);
  const [deletingDate, setDeletingDate] = useState(false);
  const [flowVariation, setFlowVariation] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showCrosscheck, setShowCrosscheck] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const [accountsData, historyData] = await Promise.all([
        fetchFlowAccounts(),
        fetchFlowAccountsHistory()
      ]);
      setAccounts(accountsData || []);
      setAccountsHistory(historyData || []);
      
      // Initialize table data with existing values
      initializeTableData(accountsData || [], historyData || []);
    } catch (error) {
      toast.error('Failed to load accounts data');
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const initializeTableData = (accountsData, historyData) => {
    const newTableData = {};
    
    // Get all unique dates from existing history
    const allDates = new Set();
    historyData.forEach(history => {
      allDates.add(history.historical_date);
    });
    
    accountsData.forEach(account => {
      newTableData[account.id] = {};
      
      // Initialize with existing history records
      historyData.forEach(history => {
        if (history.account === account.id) {
          newTableData[account.id][history.historical_date] = {
            date: history.historical_date,
            value: history.historical_value.toString()
          };
        }
      });
      
      // Initialize empty entries for dates that exist but don't have this account
      allDates.forEach(date => {
        if (!newTableData[account.id][date]) {
          newTableData[account.id][date] = {
            date: date,
            value: ''
          };
        }
      });
    });
    
    setTableData(newTableData);
  };

  useEffect(() => {
    loadData();
  }, []);

  useImperativeHandle(ref, () => ({
    refreshData: loadData
  }));

  const handleModalSuccess = () => {
    loadData();
  };

  const handleDeleteDate = (date) => {
    setDateToDelete(date);
    setShowDeleteDateModal(true);
  };

  const confirmDeleteDate = async () => {
    if (!dateToDelete) return;

    setDeletingDate(true);
    try {
      // Remove the date from tableData for all accounts
      setTableData(prev => {
        const newData = { ...prev };
        Object.keys(newData).forEach(accountId => {
          if (newData[accountId][dateToDelete]) {
            delete newData[accountId][dateToDelete];
          }
        });
        return newData;
      });

      // Delete all history records for this date
      const recordsToDelete = accountsHistory.filter(history => history.historical_date === dateToDelete);
      for (const record of recordsToDelete) {
        try {
          await deleteFlowAccountHistory(record.id);
        } catch (deleteError) {
          console.warn('Failed to delete record:', record.id, deleteError);
        }
      }

      toast.success(`Date ${dateToDelete} and all related records deleted successfully`);
      
      // Reload data to ensure consistency
      loadData();
    } catch (error) {
      toast.error('Failed to delete date and records');
      console.error('Error deleting date:', error);
    } finally {
      setDeletingDate(false);
      setShowDeleteDateModal(false);
      setDateToDelete(null);
    }
  };

  const cancelDeleteDate = () => {
    if (!deletingDate) {
      setShowDeleteDateModal(false);
      setDateToDelete(null);
    }
  };

  const handleTableDataChange = (accountId, date, field, value) => {
    setTableData(prev => {
      const newData = { ...prev };
      
      // Ensure account exists
      if (!newData[accountId]) {
        newData[accountId] = {};
      }
      
      // Ensure date entry exists
      if (!newData[accountId][date]) {
        newData[accountId][date] = {
          date: date,
          value: ''
        };
      }
      
      // Update the specific field
      newData[accountId][date] = {
        ...newData[accountId][date],
        [field]: value
      };
      
      return newData;
    });
  };

  const handleFlowVariationChange = (accountId, value) => {
    setFlowVariation(prev => ({
      ...prev,
      [accountId]: value
    }));
  };

  const handleSaveAllChanges = async () => {
    const allRecords = [];
    
    // Collect all records from table data
    Object.entries(tableData).forEach(([accountId, accountData]) => {
      Object.entries(accountData).forEach(([date, recordData]) => {
        // Skip if no date is set (for new records column)
        if (!recordData.date || recordData.date.trim() === '') {
          return;
        }
        
        // Skip if no value is provided
        if (!recordData.value || recordData.value.toString().trim() === '' || isNaN(parseFloat(recordData.value))) {
          return;
        }
        
        const parsedValue = parseFloat(recordData.value);
        
        // Include the record (including value 0)
        allRecords.push({
          account: parseInt(accountId),
          historical_date: recordData.date,
          historical_value: parsedValue
        });
      });
    });

    // Include new date input if valid (only if not already in tableData)
    if (newDateInput && newDateInput.trim() !== '') {
      accounts.forEach(account => {
        // Check if this account+date combination is already in tableData
        const alreadyExists = tableData[account.id] && tableData[account.id][newDateInput];
        
        if (!alreadyExists) {
          const newValue = getTableValue(account.id, newDateInput);
          if (newValue && newValue.toString().trim() !== '' && !isNaN(parseFloat(newValue))) {
            allRecords.push({
              account: parseInt(account.id),
              historical_date: newDateInput,
              historical_value: parseFloat(newValue)
            });
          }
        }
      });
    }

    if (allRecords.length === 0) {
      toast.error('No valid data to save');
      return;
    }

    // Remove duplicates based on account + date combination
    const uniqueRecords = [];
    const seen = new Set();
    
    allRecords.forEach(record => {
      const key = `${record.account}-${record.historical_date}`;
      if (!seen.has(key)) {
        seen.add(key);
        uniqueRecords.push(record);
      }
    });

    setAddingHistory(true);
    try {
      // Delete all existing history records in one batch operation
      await deleteAllFlowAccountHistory();
      
      // Create all new records in one batch operation
      await createBatchFlowAccountHistory(uniqueRecords);
      
      toast.success(`${uniqueRecords.length} record(s) saved successfully`);
      
      // Clear new date input
      setNewDateInput('');
      
      // Reload data
      loadData();
    } catch (error) {
      console.error('Error details:', error.response?.data);
      if (error.response?.data?.error) {
        toast.error(`Error: ${error.response.data.error}`);
      } else if (error.response?.data?.non_field_errors) {
        toast.error(`Validation error: ${error.response.data.non_field_errors.join(', ')}`);
      } else if (error.response?.data?.account) {
        toast.error(`Account error: ${error.response.data.account.join(', ')}`);
      } else if (error.response?.data?.historical_date) {
        toast.error(`Date error: ${error.response.data.historical_date.join(', ')}`);
      } else if (error.response?.data?.historical_value) {
        toast.error(`Value error: ${error.response.data.historical_value.join(', ')}`);
      } else {
        toast.error(`Failed to save changes: ${error.response?.data?.message || error.message}`);
      }
    } finally {
      setAddingHistory(false);
    }
  };

  const getTableValue = (accountId, date) => {
    if (!date) return '';
    return tableData[accountId]?.[date]?.value || '';
  };


  const getTableDate = (accountId, date) => {
    return tableData[accountId]?.[date]?.date || date;
  };

  const getUniqueDates = () => {
    const dates = new Set();
    
    // Collect all dates from existing table data
    Object.values(tableData).forEach(accountData => {
      Object.keys(accountData).forEach(date => {
        // Exclude the current new date input to avoid duplication
        if (date && date !== newDateInput && date.trim() !== '') {
          dates.add(date);
        }
      });
    });
    
    // Also include any existing history dates that might not be in tableData yet
    accountsHistory.forEach(history => {
      if (history.historical_date && history.historical_date !== newDateInput) {
        dates.add(history.historical_date);
      }
    });
    
    return Array.from(dates).sort((a, b) => new Date(a) - new Date(b));
  };



  const formatDate = (dateString) => {
    // Parse the date string directly to avoid timezone issues
    const [year, month, day] = dateString.split('-');
    return new Date(year, month - 1, day).toLocaleDateString('pt-BR');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const calculateBalanceDifference = () => {
    const sortedDates = getUniqueDates();
    
    // Need at least 2 dates to calculate difference
    if (sortedDates.length < 2) {
      return null;
    }

    const latestDate = sortedDates[sortedDates.length - 1]; // Last date (most recent)
    const previousDate = sortedDates[sortedDates.length - 2]; // Second to last date

    const calculations = [];

    accounts.forEach(account => {
      const initialValue = parseFloat(getTableValue(account.id, previousDate)) || 0;
      const flowVariationValue = parseFloat(flowVariation[account.id]) || 0;
      const endCalculated = initialValue + flowVariationValue;
      const endReference = parseFloat(getTableValue(account.id, latestDate)) || 0;
      const difference = endCalculated - endReference;
      
      calculations.push({
        accountName: account.account_name,
        initialValue,
        flowVariationValue,
        endCalculated,
        endReference,
        difference
      });
    });

    return {
      latestDate,
      previousDate,
      calculations
    };
  };

  return (
    <>
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <SpinnerLoad />
          </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                    {/* a. Account Configuration */}
                    <div className="min-w-0">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-gray-700">a. Account Configuration</h3>
                        <button
                          onClick={() => setIsModalOpen(true)}
                          className="flex items-center gap-1 px-3 py-1 bg-green-800 text-white rounded text-xs hover:bg-green-900"
                        >
                          <Cog6ToothIcon className="h-3 w-3" />
                          Manage
                        </button>
                      </div>
                      
                      {loading ? (
                        <div className="flex items-center justify-center h-20">
                          <SpinnerLoad />
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {accounts.map((account) => {
                            const isLinked = account.openfinance_item && account.openfinance_account;
                            return (
                              <div key={account.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                <div className="flex items-center gap-3">
                                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center">
                                    <span className="text-xs font-medium text-blue-600">
                                      {account.account_name.charAt(0).toUpperCase()}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-sm font-medium text-gray-800">{account.account_name}</span>
                                    {isLinked && (
                                      <div className="text-xs text-gray-600">
                                        {account.openfinance_item_name || account.openfinance_item} | {account.openfinance_account_name || account.openfinance_account}
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  {isLinked ? (
                                    <span className="flex items-center gap-1 text-xs text-green-600 bg-green-100 px-2 py-1 rounded">
                                      ✓ Linked
                                    </span>
                                  ) : (
                                    <span className="text-xs text-gray-500">Not configured</span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* b. Account Balance + Crosscheck */}
                    <div className="min-w-0">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">b. Account Balance</h3>
              
              {/* Enhanced History Table - Transposed */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                
                {/* Table Content */}
                <div className="overflow-x-auto">
                  <table className="w-full table-fixed">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-2 py-2 text-left text-xs font-semibold text-gray-600 tracking-wider border-r border-gray-200 w-24">
                          Date
                        </th>
                        {accounts.map((account) => (
                                  <th key={account.id} className="px-1 py-1 text-center text-xs font-semibold text-gray-600 tracking-wider border-r border-gray-200 bg-gray-100" style={{ width: `calc((100% - 96px) / ${accounts.length})` }}>
                                    <div className="flex flex-col items-center gap-0.5">
                                      <div className="flex-shrink-0 h-4 w-4 bg-blue-100 rounded-full flex items-center justify-center">
                                        <span className="text-xs font-medium text-blue-600">
                                          {account.account_name.charAt(0).toUpperCase()}
                                        </span>
                                      </div>
                                      <span className="text-xs font-medium truncate">{account.account_name}</span>
                                    </div>
                                  </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {getUniqueDates().map((date, index) => (
                        <tr key={date} className={`hover:bg-gray-50 transition-colors duration-150 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                          <td className="border-r border-gray-200 w-24">
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => handleDeleteDate(date)}
                                disabled={addingHistory || deletingDate}
                                className={`p-1 rounded transition-colors duration-200 flex-shrink-0 ${
                                  (addingHistory || deletingDate)
                                    ? 'text-gray-400 cursor-not-allowed'
                                    : 'text-red-600 hover:text-red-900 hover:bg-red-50'
                                }`}
                                title="Delete this date and all related records"
                              >
                                <TrashIcon className="h-3 w-3" />
                              </button>
                              <span className="text-xs text-gray-700 font-medium">
                                {formatDate(date)}
                              </span>
                            </div>
                          </td>
                          {accounts.map((account) => {
                            const currentValue = getTableValue(account.id, date);
                            return (
                              <td key={account.id} className="text-center border-r border-gray-200" style={{ width: `calc((100% - 96px) / ${accounts.length})` }}>
                                <input
                                  type="text"
                                  value={currentValue}
                                  onChange={(e) => handleTableDataChange(account.id, date, 'value', e.target.value)}
                                  placeholder="0.00"
                                  className="w-full h-full bg-transparent border-none outline-none text-center text-sm font-medium focus:bg-blue-50"
                                  disabled={addingHistory}
                                />
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                      
                      {/* New Date Row - Always visible */}
                      <tr className="bg-blue-50 hover:bg-blue-100 transition-colors duration-150 border-t-2 border-blue-200">
                        <td className="border-r border-gray-200 w-24">
                          <input
                            type="date"
                            value={newDateInput}
                            onChange={(e) => setNewDateInput(e.target.value)}
                            className="w-full h-full bg-transparent border-none outline-none text-xs px-1 py-1 focus:bg-blue-100"
                            disabled={addingHistory}
                            placeholder="Select date"
                          />
                        </td>
                        {accounts.map((account) => (
                          <td key={account.id} className="text-center border-r border-gray-200" style={{ width: `calc((100% - 96px) / ${accounts.length})` }}>
                            <input
                              type="text"
                              value={getTableValue(account.id, newDateInput)}
                              onChange={(e) => handleTableDataChange(account.id, newDateInput, 'value', e.target.value)}
                              placeholder="0.00"
                              className="w-full h-full bg-transparent border-none outline-none text-center text-sm font-medium focus:bg-blue-100"
                              disabled={addingHistory}
                            />
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>

                        {/* Footer */}
                        <div className="bg-gray-50 px-2 py-1 border-t border-gray-200">
                  <div className="flex justify-end">
                    <button
                      onClick={handleSaveAllChanges}
                      disabled={addingHistory}
                      className={`flex items-center gap-1 px-3 py-1 text-xs font-medium rounded transition-all duration-200 ${
                        addingHistory
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-green-800 text-white hover:bg-green-900'
                      }`}
                    >
                      <CheckIcon className="h-3 w-3" />
                      {addingHistory ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </div>
              </div>

              {/* c. Crosscheck considering Flows Section */}
              <div className="mt-4">
                <button
                  onClick={() => setShowCrosscheck(!showCrosscheck)}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-xl transition-all duration-200 text-sm font-medium mb-3 shadow-sm hover:shadow-md"
                >
                  {showCrosscheck ? (
                    <ChevronDownIcon className="h-4 w-4" />
                  ) : (
                    <ChevronRightIcon className="h-4 w-4" />
                  )}
                  Crosscheck
                </button>
                
                <div className={`transition-all duration-300 ease-in-out overflow-hidden ${
                  showCrosscheck 
                    ? 'max-h-screen opacity-100 transform translate-y-0' 
                    : 'max-h-0 opacity-0 transform -translate-y-2'
                }`}>
                  <CustomAccountsCrosscheck
                    accounts={accounts}
                    flowVariation={flowVariation}
                    handleFlowVariationChange={handleFlowVariationChange}
                    addingHistory={addingHistory}
                    calculateBalanceDifference={calculateBalanceDifference}
                    formatDate={formatDate}
                  />
                </div>
              </div>
                    </div>
          </div>
        )}

      {/* Delete Date Confirmation Modal */}
      <ModalConfirm
        isOpen={showDeleteDateModal}
        onClose={cancelDeleteDate}
        onConfirm={confirmDeleteDate}
        title="Delete Date"
        message={`Are you sure you want to delete the date "${dateToDelete}" and all related records? This action cannot be undone.`}
        confirmText={deletingDate ? "Deleting..." : "Delete"}
        cancelText="Cancel"
        confirmButtonClass={`${deletingDate ? 'bg-gray-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'} text-white shadow`}
        cancelButtonClass={`px-4 py-2 rounded-xl border border-gray-300 ${deletingDate ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`}
        disabled={deletingDate}
      />

      {/* Manage Accounts Modal */}
      <CustomModalAccountsManage
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={handleModalSuccess}
      />

    </>
  );
});

export default CustomAccountsStatus;
